// src/features/records/components/ShareDialog/components/PeopleGroupPicker.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import friendshipService from '../../../../../services/friendshipService';
import { useGroups } from '../../../../groups';
import { useFloatingMenu } from '../../../../../hooks/useFloatingMenu';
import styles from './PeopleGroupPicker.module.css';
import { Avatar, AVATAR_VARIANTS } from '../../../../../components/avatars';

const EMPTY_EXCLUDE_IDS = Object.freeze([]);

const PeopleGroupPicker = ({ 
  value = [], 
  onChange, 
  placeholder,
  disabled = false,
  excludeIds = EMPTY_EXCLUDE_IDS
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const searchRequestRef = useRef(0);
  
  const { searchGroups } = useGroups();

  const resetSearchUiState = useCallback(() => {
    setSuggestions((prev) => (prev.length === 0 ? prev : []));
    setLoading((prev) => (prev ? false : prev));
    setHighlightedIndex((prev) => (prev === -1 ? prev : -1));
  }, []);

  // Use Floating UI hook for positioning and state management
  const {
    open,
    setOpen,
    refs,
    floatingProps,
  } = useFloatingMenu({
    placement: "bottom-start",
    sameWidth: true,
    exactWidth: true,
    minWidthPx: 240,
    strategy: "fixed",
    offsetValue: 6,
    maxHeight: 320,
  });

  // Debounced search function
  const performSearch = useCallback(
    async (searchTerm, requestId) => {
      const trimmedTerm = searchTerm.trim();
      if (requestId !== searchRequestRef.current) return;

      if (!trimmedTerm) {
        resetSearchUiState();
        return;
      }

      setLoading(true);
      try {
        const [users, matchingGroups] = await Promise.all([
          friendshipService.searchFriends(trimmedTerm, { limitCount: 50 }),
          Promise.resolve(searchGroups(trimmedTerm)),
        ]);
        if (requestId !== searchRequestRef.current) return;

        const selectedIds = new Set(value.map((p) => `${p.type}:${p.id}`));
        const excluded = new Set(excludeIds);

        const filteredUsers = users
          .filter(
            (u) =>
              !selectedIds.has(`user:${u.id}`) && !excluded.has(`user:${u.id}`)
          )
          .map((u) => ({
            type: "user",
            id: u.id,
            name:
              u.displayName ||
              u.username ||
              t("records.share.picker.unknown_user", "Unknown user"),
            email: u.email,
            avatar: u.profilePictureUrl,
          }));

        const filteredGroups = matchingGroups
          .filter(
            (g) =>
              !selectedIds.has(`group:${g.id}`) &&
              !excluded.has(`group:${g.id}`)
          )
          .map((g) => ({
            type: "group",
            id: g.id,
            name: g.name,
            memberCount: g.memberCount || 0,
            icon: g.icon,
            avatar: g.icon,
          }));

        const combined = [
          ...filteredUsers.slice(0, 5),
          ...filteredGroups.slice(0, 5),
        ];
        setSuggestions(combined);
        setHighlightedIndex(-1);
      } catch (err) {
        if (requestId !== searchRequestRef.current) return;
        console.error("Error searching:", err);
        setSuggestions([]);
      } finally {
        if (requestId !== searchRequestRef.current) return;
        setLoading(false);
      }
    },
    [excludeIds, resetSearchUiState, searchGroups, t, value]
  );

  // Handle input change with debouncing
  useEffect(() => {
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (inputValue.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(inputValue, requestId);
      }, 300);
    } else {
      resetSearchUiState();
    }
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [inputValue, performSearch, resetSearchUiState]);

  useEffect(() => {
    return () => {
      searchRequestRef.current += 1;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const getOptionId = (i) =>
    i >= 0 && i < suggestions.length
      ? `people-group-option-${suggestions[i].type}-${suggestions[i].id}`
      : undefined;

  const handleSelect = (item) => {
    const principal = {
      ...item,
      type: item.type,
      id: item.id,
      name: item.name,
    };
    onChange([...value, principal]);
    setInputValue("");
    setSuggestions([]);
    setOpen(false); // close via hook
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleRemove = (index) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  };

  // Keyboard nav for listbox/combobox
  const handleKeyDown = (e) => {
    const hasList = open && suggestions.length > 0;
    if (!hasList) {
      if (e.key === "Backspace" && !inputValue && value.length > 0) {
        handleRemove(value.length - 1);
      }
      if (e.key === "ArrowDown") {
        // open the popup to start navigating
        setOpen(true);
        e.preventDefault();
      }
      if (e.key === "Escape") {
        setOpen(false);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.container}>
      {/* Make the wrapper the floating reference so panel matches its width */}
      <div
        ref={refs.setReference}
        className={styles.inputWrapper}
        onMouseDown={() => !disabled && inputRef.current?.focus()} // focus input on wrapper click
      >
        <Search className={styles.searchIcon} size={16} />
        
        <div className={styles.chipsAndInput}>
          {value.map((principal, index) => (
            <div
              key={`${principal.type}:${principal.id}`}
              className={styles.chip}
            >
              <div className={styles.chipAvatar}>
                {principal.type === "group" ? (
                  <Avatar
                    variant={AVATAR_VARIANTS.GROUP}
                    name={principal.name}
                    src={principal.avatar || principal.icon}
                    size="xs"
                    shape="rounded"
                    textColor="var(--text-inverse)"
                  />
                ) : (
                  <Avatar
                    name={principal.name}
                    src={principal.avatar}
                    size="xs"
                    textColor="var(--text-inverse)"
                  />
                )}
              </div>
              <span className={styles.chipText}>{principal.name}</span>
              <button
                onClick={() => handleRemove(index)}
                className={styles.chipRemove}
                disabled={disabled}
                aria-label={t("records.share.picker.remove_principal_aria", {
                  name: principal.name,
                  defaultValue: `Remove ${principal.name}`,
                })}
              >
                <X size={12} />
              </button>
            </div>
          ))}

          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (!open) setOpen(true); // typing should open
            }}
            onFocus={() => !disabled && setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={
              value.length === 0
                ? placeholder ||
                  t(
                    "records.share.quick_add.search_placeholder",
                    "Search for people and groups..."
                  )
                : ""
            }
            disabled={disabled}
            className={styles.input}
            role="combobox"
            aria-expanded={open && (inputValue.trim() || loading)}
            aria-controls="people-group-listbox"
            aria-autocomplete="list"
            aria-activedescendant={getOptionId(highlightedIndex)}
            aria-describedby="people-group-helper"
          />
        </div>
      </div>

      {/* Dropdown – positioned by Floating UI */}
      {open && (inputValue.trim() || loading) && (
        <div
          ref={refs.setFloating}
          {...floatingProps}
          className={styles.dropdown}
          role="listbox"
          id="people-group-listbox"
        >
          <div className={styles.dropdownInner}>
            {loading ? (
              <div className={styles.dropdownLoading}>
                {t("records.share.picker.searching", "Searching...")}
              </div>
            ) : suggestions.length > 0 ? (
              <>
                {/* People */}
                {suggestions.some((s) => s.type === "user") && (
                <>
                  <div className={styles.dropdownSection}>
                    {t("records.share.picker.people_section", "People")}
                  </div>
                  {suggestions
                    .filter((s) => s.type === "user")
                    .map((user) => {
                      const actualIndex = suggestions.findIndex(
                        (s) => s === user
                      );
                      const optionId = getOptionId(actualIndex);
                      return (
                        <button
                          key={`user:${user.id}`}
                          id={optionId}
                          className={`${styles.dropdownItem} ${
                            highlightedIndex === actualIndex
                              ? styles.highlighted
                              : ""
                          }`}
                          onClick={() => handleSelect(user)}
                          onMouseEnter={() => setHighlightedIndex(actualIndex)}
                          role="option"
                          aria-selected={highlightedIndex === actualIndex}
                        >
                          <Avatar
                            name={user.name}
                            src={user.avatar}
                            size="sm"
                            alt={t("records.share.picker.user_avatar_alt", {
                              name: user.name,
                              defaultValue: `${user.name} avatar`,
                            })}
                            textColor="var(--text-inverse)"
                            className={styles.dropdownAvatar}
                          />
                          <div className={styles.itemContent}>
                            <div className={styles.itemName}>{user.name}</div>
                            <div className={styles.itemMeta}>
                              {user.email && <span>{user.email}</span>}
                              <span className={`${styles.itemBadge} ${styles.friend}`}>
                                {t("records.share.picker.friend_badge", "Friend")}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </>
              )}

              {/* Groups */}
              {suggestions.some((s) => s.type === "group") && (
                <>
                  <div className={styles.dropdownSection}>
                    {t("records.share.picker.groups_section", "Groups")}
                  </div>
                  {suggestions
                    .filter((s) => s.type === "group")
                    .map((group) => {
                      const actualIndex = suggestions.findIndex(
                        (s) => s === group
                      );
                      const optionId = getOptionId(actualIndex);
                      return (
                        <button
                          key={`group:${group.id}`}
                          id={optionId}
                          className={`${styles.dropdownItem} ${
                            highlightedIndex === actualIndex
                              ? styles.highlighted
                              : ""
                          }`}
                          onClick={() => handleSelect(group)}
                          onMouseEnter={() => setHighlightedIndex(actualIndex)}
                          role="option"
                          aria-selected={highlightedIndex === actualIndex}
                        >
                          <Avatar
                            variant={AVATAR_VARIANTS.GROUP}
                            name={group.name}
                            src={group.icon}
                            size="sm"
                            shape="rounded"
                            alt={t("records.share.picker.group_avatar_alt", {
                              name: group.name,
                              defaultValue: `${group.name} group avatar`,
                            })}
                            textColor="var(--text-inverse)"
                            className={styles.dropdownAvatar}
                          />
                          <div className={styles.itemContent}>
                            <div className={styles.itemName}>{group.name}</div>
                            <div className={styles.itemMeta}>
                              {t("records.share.picker.member_count", {
                                count: group.memberCount,
                                defaultValue: `${group.memberCount} members`,
                              })}
                              <span className={styles.itemType}>
                                {t("records.share.picker.group_type", "Group")}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </>
              )}
              </>
            ) : (
              <div className={styles.dropdownEmpty}>
                {t("records.share.picker.no_results", "No results found")}
              </div>
            )}
          </div>
        </div>
      )}

      <span id="people-group-helper" className={styles.srOnly}>
        {t(
          "records.share.picker.helper",
          "Type to search for people and groups. Use arrow keys to navigate suggestions."
        )}
      </span>
    </div>
  );
};

export default PeopleGroupPicker;
